import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, Mail, CheckCircle2, Clock, ShieldAlert, ShieldCheck, Search, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api, { hubError } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

const STAGES = {
  al_dia: { label: 'Al día', className: 'bg-[#1F9D7A]/10 text-[#1F9D7A] border-[#1F9D7A]/20', Icon: CheckCircle2 },
  en_mora: { label: 'En mora', className: 'bg-[#C58B2A]/10 text-[#C58B2A] border-[#C58B2A]/20', Icon: Clock },
  suspendida: { label: 'Suite bloqueada', className: 'bg-destructive/10 text-destructive border-destructive/20', Icon: ShieldAlert },
  restringida: { label: 'App restringida', className: 'bg-destructive/15 text-destructive border-destructive/30', Icon: ShieldAlert },
};

const money = (v) => `${Number(v || 0).toFixed(2)} USD`;
const day = (v) => (v ? new Date(v).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function CollectionsPage() {
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mailOk, setMailOk] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [access, setAccess] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/billing/collections')
      .then(res => {
        setRows(res.data?.data || []);
        setSummary(res.data?.summary || null);
        setMailOk(res.data?.mail_configured !== false);
      })
      .catch(err => toast.error(hubError(err, 'No se pudo cargar la cobranza')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = (row) => {
    setDetail(row);
    setDetailData(null);
    api.get(`/api/billing/collections/${row.church_id}`)
      .then(res => setDetailData(res.data))
      .catch(err => toast.error(hubError(err, 'No se pudo cargar el detalle')));
  };

  const remind = async (row) => {
    setBusy(`mail-${row.church_id}`);
    try {
      const res = await api.post(`/api/billing/collections/${row.church_id}/remind`);
      toast.success(`Enviado: ${res.data?.data?.sent} a ${res.data?.data?.recipients?.join(', ')}`);
    } catch (err) {
      toast.error(hubError(err, 'No se pudo enviar el aviso'));
    } finally { setBusy(null); }
  };

  const changeAccess = async () => {
    const { row, status } = access;
    setAccess(null);
    setBusy(`access-${row.church_id}`);
    try {
      await api.patch(`/api/billing/access/${row.church_id}`, { status, reason: 'billing_manual' });
      toast.success(status === 'active' ? 'Acceso reactivado en la app' : 'Acceso restringido en la app');
      load();
    } catch (err) {
      toast.error(hubError(err, 'No se pudo cambiar el acceso'));
    } finally { setBusy(null); }
  };

  const filtered = rows.filter(r => {
    if (filter !== 'all' && r.stage !== filter) return false;
    const q = search.trim().toLowerCase();
    return !q || `${r.church_name} ${r.email || ''}`.toLowerCase().includes(q);
  });

  const cards = [
    { key: 'all', label: 'Congregaciones de pago', value: summary?.total ?? 0, tone: 'text-[#056A84]' },
    { key: 'en_mora', label: 'En mora', value: summary?.en_mora ?? 0, tone: 'text-[#C58B2A]' },
    { key: 'suspendida', label: 'Suite bloqueada', value: summary?.suspendidas ?? 0, tone: 'text-destructive' },
    { key: 'restringida', label: 'App restringida', value: summary?.restringidas ?? 0, tone: 'text-destructive' },
  ];

  return (
    <div data-testid="collections-page">
      <TopBar title="Cobranza" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5 space-y-5">
        {!mailOk && (
          <Card className="border-[#C58B2A]/40 bg-[#C58B2A]/5">
            <CardContent className="p-4 text-sm text-[#8a6418]" data-testid="collections-mail-warning">
              El correo transaccional no está configurado: los avisos no saldrán hasta cargar la clave de Resend.
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {cards.map(c => (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilter(c.key === 'all' ? 'all' : (filter === c.key ? 'all' : c.key))}
              className={`text-left rounded-xl border p-4 transition-colors hover:bg-accent/40 ${filter === c.key ? 'ring-2 ring-[#056A84]/40' : ''}`}
              data-testid={`collections-card-${c.key}`}
            >
              <p className={`text-2xl font-semibold ${c.tone}`}>{c.value}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{c.label}</p>
            </button>
          ))}
          <div className="rounded-xl border p-4 bg-[#062B36] text-white">
            <p className="text-2xl font-semibold" data-testid="collections-amount-due">{money(summary?.amount_due)}</p>
            <p className="text-[11px] text-white/60 mt-1">Por cobrar</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar congregación..." className="pl-9" data-testid="collections-search" />
          </div>
          {filter !== 'all' && (
            <Button variant="outline" size="sm" onClick={() => setFilter('all')} data-testid="collections-clear-filter">Ver todas</Button>
          )}
          <Button variant="outline" size="sm" className="ml-auto" onClick={load} data-testid="collections-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
        </div>

        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="collections-empty">
            No hay congregaciones que coincidan.
          </CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="collections-list">
            {filtered.map(row => {
              const stage = STAGES[row.stage] || STAGES.al_dia;
              const StageIcon = stage.Icon;
              return (
                <div key={row.church_id} className="rounded-xl border p-4 flex flex-wrap items-center gap-3" data-testid={`collections-row-${row.church_id}`}>
                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm font-medium truncate">{row.church_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {row.members ?? 0} miembros · {money(row.monthly)}/mes
                      {row.payer?.email ? ` · ${row.payer.email}` : row.email ? ` · ${row.email}` : ''}
                    </p>
                  </div>
                  <div className="text-xs text-muted-foreground min-w-[140px]">
                    <p>Próximo corte: <span className="text-foreground">{day(row.next_due_date)}</span></p>
                    <p>Último pago: {day(row.last_payment)}</p>
                  </div>
                  <div className="min-w-[120px]">
                    <Badge variant="outline" className={`${stage.className} text-[11px]`} data-testid={`collections-stage-${row.church_id}`}>
                      <StageIcon className="w-3 h-3 mr-1" /> {stage.label}
                    </Badge>
                    {row.days_overdue > 0 && (
                      <p className="text-[11px] text-destructive mt-1">{row.days_overdue} día(s) de atraso</p>
                    )}
                    {row.in_trial && row.days_overdue === 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1">Cortesía hasta {day(row.trial_until)}</p>
                    )}
                  </div>
                  <div className="min-w-[90px] text-right">
                    <p className="text-sm font-semibold">{money(row.amount_due)}</p>
                    <p className="text-[11px] text-muted-foreground">{row.pending_invoices} factura(s)</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy === `mail-${row.church_id}`} onClick={() => remind(row)} data-testid={`collections-remind-${row.church_id}`}>
                      {busy === `mail-${row.church_id}` ? <Loader2 className="w-3.5 h-3.5 spinner mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
                      Reenviar aviso
                    </Button>
                    {row.access_status === 'active' ? (
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive" disabled={busy === `access-${row.church_id}`} onClick={() => setAccess({ row, status: 'restricted' })} data-testid={`collections-restrict-${row.church_id}`}>
                        <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Restringir
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-[#1F9D7A]" disabled={busy === `access-${row.church_id}`} onClick={() => setAccess({ row, status: 'active' })} data-testid={`collections-reactivate-${row.church_id}`}>
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Reactivar
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => openDetail(row)} data-testid={`collections-detail-${row.church_id}`}>
                      <FileText className="w-3.5 h-3.5 mr-1" /> Historial
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Sheet open={!!detail} onOpenChange={(open) => { if (!open) { setDetail(null); setDetailData(null); } }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{detail?.church_name}</SheetTitle>
            <SheetDescription>Facturas, órdenes de Caura Pay y avisos enviados.</SheetDescription>
          </SheetHeader>
          {!detailData ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 spinner text-[#056A84]" /></div>
          ) : (
            <div className="space-y-6 py-4" data-testid="collections-detail-panel">
              <section>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Facturas</h3>
                <div className="space-y-1.5">
                  {(detailData.state?.invoices || []).length === 0 && <p className="text-sm text-muted-foreground">Sin facturas emitidas.</p>}
                  {(detailData.state?.invoices || []).map(i => (
                    <div key={i.id} className="flex items-center justify-between text-sm border rounded-lg px-3 py-2">
                      <span>{day(i.period_start)} → {day(i.period_end)}</span>
                      <span className="text-muted-foreground text-xs">vence {day(i.due_date)}</span>
                      <span className="font-medium">{money(i.amount_usd)}</span>
                      <Badge variant="outline" className="text-[10px]">{i.status}</Badge>
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Órdenes de Caura Pay</h3>
                <div className="space-y-1.5">
                  {(detailData.orders || []).length === 0 && <p className="text-sm text-muted-foreground">Sin órdenes creadas.</p>}
                  {(detailData.orders || []).map(o => (
                    <div key={o.order_id} className="border rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs">{o.code || o.reference}</span>
                        <Badge variant="outline" className="text-[10px]">{o.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {money(o.amount_usd)}{o.amount_ves ? ` · ${o.amount_ves.toFixed(2)} Bs` : ''}
                        {o.payment_method ? ` · ${o.payment_method}` : ''}
                        {o.payment_reference ? ` · ref ${o.payment_reference}` : ''} · {day(o.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Avisos enviados</h3>
                <div className="space-y-1.5">
                  {(detailData.emails || []).length === 0 && <p className="text-sm text-muted-foreground">Sin avisos registrados.</p>}
                  {(detailData.emails || []).map((e, idx) => (
                    <div key={idx} className="border rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">{e.subject}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{e.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{e.recipient} · {day(e.created_at)}{e.error ? ` · ${e.error}` : ''}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!access} onOpenChange={() => setAccess(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {access?.status === 'active' ? '¿Reactivar el acceso?' : '¿Restringir el acceso en la app?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {access?.status === 'active'
                ? `Los miembros de ${access?.row?.church_name} vuelven a usar la app con normalidad.`
                : `Los miembros de ${access?.row?.church_name} verán el acceso limitado hasta que se regularice el pago.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={changeAccess} data-testid="collections-access-confirm">Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
